import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  IconButton
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import CustomTableWithPagination from "../../../components/customTable/CustomTable";
import CopyToClipboardWrapper from "../../../components/copyToClipboardWrapper/CopyToClipboardWrapper";
// import { format } from "date-fns";
import { getFormattedDate } from "../../../utils/commonFunctions";
import CustomButton from "../../../components/CustomButton/Button";
import Loader from "../../../components/loader/Loader";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

interface GiftCardData {
  RemainingBalance: number;
  BrandName: string;
  GiftCardNumber: string;
  GiftCardTokenAmount: number;
  PurchaseDate: string;
  EstimateAmount: number;
  RemainingAmount: number;
  GiftCardTokenId?: number;
}

function UserGiftCardsPage() {
  const { userId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const adminUserIdFromUrl = searchParams.get("aid");
  const [giftCards, setGiftCards] = useState<GiftCardData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<GiftCardData | null>(null);
  const [spendHistory, setSpendHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateAmount, setUpdateAmount] = useState<string>("");
  const [updateDate, setUpdateDate] = useState<string>("");
  const [notification, setNotification] = useState<{
    type: string | null;
    message: string | null;
  }>({ type: null, message: null });

  const fetchGiftCards = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${apiBaseUrl}/giftcard/activegiftcardtokens/${userId}`,
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data) {
        // Handle both array and object with data property
        const giftCardsData = Array.isArray(response.data)
          ? response.data
          : response.data.data || [];
        setGiftCards(giftCardsData);
      } else {
        setGiftCards([]);
      }
    } catch (err: any) {
      console.error("Failed to fetch gift cards:", err);
      setError(err.response?.data?.message || "Failed to fetch gift cards");
      setGiftCards([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchGiftCards();
  }, [userId, fetchGiftCards]);

  const handleViewHistory = async (row: GiftCardData) => {
    setSelectedCard(row);
    setHistoryOpen(true);
    if (!row?.GiftCardTokenId) return;
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${apiBaseUrl}/giftcard/giftcardspendhistory/token/${row.GiftCardTokenId}`,
        {
          headers: {
            Authorization: `bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      const data = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || response?.data?.response || [];
      setSpendHistory(data);
    } catch (e: any) {
      setSpendHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenUpdate = (row: GiftCardData) => {
    setSelectedCard(row);
    setUpdateAmount("");
    // Set default to current local datetime in yyyy-MM-ddTHH:mm format
    const now = new Date();
    const tzOffsetMin = now.getTimezoneOffset();
    const local = new Date(now.getTime() - tzOffsetMin * 60000);
    setUpdateDate(local.toISOString().slice(0, 16));
    setUpdateOpen(true);
  };

  const handleSubmitUpdate = async () => {
    if (!selectedCard?.GiftCardTokenId) return;
    const amountNumber = Number(updateAmount);
    if (Number.isNaN(amountNumber) || amountNumber <= 0) {
      setNotification({ type: "error", message: "Enter a valid amount" });
      return;
    }
    if (!updateDate) {
      setNotification({ type: "error", message: "Select a transaction date" });
      return;
    }

    // Convert to UTC ISO format (YYYY-MM-DDTHH:mm:ssZ) to match backend format
    const toUtcISO = (localDateTimeString: string) => {
      const d = new Date(localDateTimeString);
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, "0");
      const day = String(d.getUTCDate()).padStart(2, "0");
      const hh = String(d.getUTCHours()).padStart(2, "0");
      const mm = String(d.getUTCMinutes()).padStart(2, "0");
      const ss = String(d.getUTCSeconds()).padStart(2, "0");
      return `${y}-${m}-${day}T${hh}:${mm}:${ss}Z`;
    };

    try {
      setUpdateLoading(true);
      setNotification({ type: null, message: null });
      const token = localStorage.getItem("accessToken");
      await axios.post(
        `${apiBaseUrl}/giftcard/giftcardspendhistory`,
        {
          GiftCardTokenId: selectedCard.GiftCardTokenId,
          AdminEnteredAmount: amountNumber * 100, // Convert dollars to cents
          TransactionDate: toUtcISO(updateDate),
          AdminUserId: adminUserIdFromUrl
            ? Number(adminUserIdFromUrl)
            : undefined
        },
        {
          headers: {
            Authorization: `bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      setUpdateOpen(false);
      await fetchGiftCards();
    } catch (e: any) {
      setNotification({ type: "error", message: "Failed to save update" });
    } finally {
      setUpdateLoading(false);
    }
  };

  const columns = [
    {
      id: "BrandName",
      label: "Brand Name",
      minWidth: 150,
      align: "left" as const,
      format: (value: string) => value || "-"
    },
    {
      id: "GiftCardNumber",
      label: "Gift Card Number",
      minWidth: 200,
      align: "left" as const,
      format: (value: string) => (
        <div
          onClick={(e) => e?.stopPropagation()}
          style={{ display: "flex", alignItems: "center" }}
        >
          <div
            style={{
              verticalAlign: "middle",
              display: "flex",
              alignItems: "center"
            }}
          >
            {value || "-"}
          </div>
          {value && (
            <div onClick={(e) => e?.stopPropagation()}>
              <CopyToClipboardWrapper text={value} cell="GiftCardNumber" />
            </div>
          )}
        </div>
      )
    },
    {
      id: "GiftCardTokenAmount",
      label: "Gift Card Token Amount",
      minWidth: 180,
      align: "right" as const,
      format: (value: string | number | null | undefined) => {
        if (!value || value === "" || value === null || value === undefined) {
          return "-";
        }
        const numValue = Number(value);
        if (isNaN(numValue)) {
          return "-";
        }
        const dividedValue = numValue / 100;
        return `$${dividedValue.toFixed(2)}`;
      }
    },
    {
      id: "PurchaseDate",
      label: "Purchase Date",
      minWidth: 150,
      align: "center" as const,
      format: (value: string) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "EstimateBalance",
      label: "Estimate Amount",
      minWidth: 150,
      align: "right" as const,
      format: (value: number) => `$${Number(value)?.toFixed(2) || "0.00"}`
    },
    {
      id: "RemainingBalance",
      label: "Remaining Amount",
      minWidth: 150,
      align: "right" as const,
      format: (value: number) => `$${Number(value)?.toFixed(2) || "0.00"}`
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 220,
      align: "left" as const,
      format: (_: any, row: GiftCardData) => (
        <div
          onClick={(e) => e?.stopPropagation()}
          style={{ display: "flex", gap: 10, alignItems: "center" }}
        >
          <CustomButton
            callBack={() => handleViewHistory(row)}
            styles={{ margin: 0 }}
          >
            View History
          </CustomButton>
          {row?.RemainingBalance && Number(row?.RemainingBalance) > 0 && (
            <CustomButton
              callBack={() => handleOpenUpdate(row)}
              styles={{ margin: 0 }}
            >
              Update
            </CustomButton>
          )}
        </div>
      )
    }
  ];

  const handleRowClick = (row: GiftCardData) => {
    // Handle row click if needed
    console.log("Clicked row:", row);
  };

  return (
    <Box>
      <div style={{ float: "right", cursor: "pointer" }}>
        <RefreshIcon onClick={fetchGiftCards} />
      </div>
      <CustomTableWithPagination
        columns={columns}
        data={giftCards}
        apiStatus={loading ? "fetching" : "idle"}
        isShowPagination={false}
        noDataFoundMessage="No gift cards found"
        handleRowClick={handleRowClick}
      />

      {/* Spend History Modal */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
            Gift Card Spend History
          </Typography>

          <IconButton
            aria-label="close"
            onClick={() => setHistoryOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Loader />
          ) : spendHistory.length === 0 ? (
            <Typography>No history found</Typography>
          ) : (
            <CustomTableWithPagination
              columns={[
                {
                  id: "GiftCardSpendHistoryId",
                  label: "History Id",
                  minWidth: 200,
                  align: "left",
                  format: (v: string) => (
                    <div
                      onClick={(e) => e?.stopPropagation()}
                      style={{ display: "flex", gap: 5, alignItems: "center" }}
                    >
                      {v ? `${String(v).slice(-6)}` : "-"}
                      {v && (
                        <CopyToClipboardWrapper
                          text={String(v)}
                          cell="GiftCardSpendHistoryId"
                        />
                      )}
                    </div>
                  )
                },
                {
                  id: "GiftCardTokenId",
                  label: "Token Id",
                  minWidth: 100,
                  align: "right",
                  format: (v: number) => v ?? "-"
                },
                {
                  id: "TransactionDate",
                  label: "Transaction Date",
                  minWidth: 170,
                  align: "left",
                  format: (v: string) => (v ? getFormattedDate(v) : "-")
                },
                {
                  id: "UserEnteredAmount",
                  label: "User Amount",
                  minWidth: 130,
                  align: "right",
                  format: (v: number) => `$${Number(v ?? 0).toFixed(2)}`
                },
                {
                  id: "AdminUserId",
                  label: "Admin User Id",
                  minWidth: 140,
                  align: "right",
                  format: (v: number) => v ?? "-"
                },
                {
                  id: "AdminEnteredAmount",
                  label: "Admin Amount",
                  minWidth: 130,
                  align: "right",
                  format: (v: number) => {
                    const amountInDollars = Number(v ?? 0) / 100; // Convert cents to dollars
                    return `$${amountInDollars.toFixed(2)}`;
                  }
                },
                {
                  id: "UpdatedDate",
                  label: "Updated",
                  minWidth: 170,
                  align: "left",
                  format: (v: string) => (v ? getFormattedDate(v) : "-")
                }
              ]}
              data={spendHistory}
              apiStatus={"idle"}
              isShowPagination={false}
              noDataFoundMessage="No history found"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Update Balance Modal */}
      <Dialog
        open={updateOpen}
        onClose={() => !updateLoading && setUpdateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
            Enter the transaction amount
          </Typography>

          <IconButton
            aria-label="close"
            onClick={() => !updateLoading && setUpdateOpen(false)}
            disabled={updateLoading}
            sx={{ position: "absolute", right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" gap={2} flexDirection="column" sx={{ mt: 2 }}>
            <TextField
              label="Amount"
              type="number"
              value={updateAmount}
              onChange={(e) => setUpdateAmount(e.target.value)}
              inputProps={{ step: "0.01", min: 0 }}
              fullWidth
            />
            <TextField
              label="Transaction Date"
              type="datetime-local"
              value={updateDate}
              onChange={(e) => setUpdateDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            {notification.message && (
              <Typography
                color={notification.type === "error" ? "error" : "primary"}
              >
                {notification.message}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <CustomButton
            callBack={() => setUpdateOpen(false)}
            disabled={updateLoading}
          >
            Cancel
          </CustomButton>
          <CustomButton
            callBack={handleSubmitUpdate}
            disabled={updateLoading}
            styles={{ minHeight: "36px" }}
          >
            {updateLoading ? "Saving..." : "Save"}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserGiftCardsPage;
